import React, { useEffect, useState } from 'react';

import { Button, Dialog } from '@mui/material';
import type { ButtonProps, SxProps, Theme } from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER } from '~/consts/roles';
import { STEP_ENTER_CODE, STEP_SEND_SMS, STEP_SUCCESS } from '~/consts/smsOtpVerification';

import { isCustomerVerified as checkCustomerVerified } from '~/utils/verificationCookies';

import EnterCodeModal from './EnterCodeModal';
import OtpSuccessModal from './OtpSuccessModal';
import SendSmsModal from './SendSmsModal';

export type Step = 'SEND_SMS' | 'ENTER_CODE' | 'SUCCESS';

interface Props {
  user: App.User;
  buttonVariant?: ButtonProps['variant'];
  buttonSx?: SxProps<Theme>;
  startIcon?: React.ReactNode;
}

export default function VerifyCustomerButtons({ user, buttonVariant = 'contained', buttonSx, startIcon }: Props) {
  const [currentStep, setCurrentStep] = useState<Step | null>(null);
  const [isCustomerVerified, setIsCustomerVerified] = useState(false);

  const isModalOpen = Boolean(currentStep);
  const hasPhoneNumber = user.phone_prefix && user.phone;

  // Check if customer is verified via cookie
  useEffect(() => {
    setIsCustomerVerified(checkCustomerVerified(user.id_member));
  }, [user.id_member]);

  // Update verified state when verification succeeds
  useEffect(() => {
    if (currentStep === STEP_SUCCESS) {
      setIsCustomerVerified(true);
    }
  }, [currentStep]);

  const handleOpen = () => {
    setCurrentStep(STEP_SEND_SMS);
  };

  const handleClose = () => {
    setCurrentStep(null);
  };

  return (
    <>
      <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
        <Button
          variant={buttonVariant}
          startIcon={startIcon}
          onClick={handleOpen}
          disabled={isModalOpen || !hasPhoneNumber}
          sx={[
            buttonVariant === 'contained'
              ? {
                  backgroundColor: isCustomerVerified ? 'action.disabledBackground' : 'info.dark',
                }
              : {},
            ...(Array.isArray(buttonSx) ? buttonSx : [buttonSx]),
          ]}
        >
          Verify Customer - SMS
        </Button>
      </PermissionedComponent>

      <Dialog open={isModalOpen} onClose={handleClose} fullWidth maxWidth="sm">
        {currentStep === STEP_SEND_SMS && (
          <SendSmsModal
            onClose={handleClose}
            setCurrentStep={setCurrentStep}
            phonePrefix={user.phone_prefix}
            phone={user.phone}
            customerId={user.id_member}
            customerName={user.fullName}
          />
        )}
        {currentStep === STEP_ENTER_CODE && (
          <EnterCodeModal
            onClose={handleClose}
            setCurrentStep={setCurrentStep}
            phonePrefix={user.phone_prefix}
            phone={user.phone}
            customerId={user.id_member}
          />
        )}
        {currentStep === STEP_SUCCESS && <OtpSuccessModal onClose={handleClose} />}
      </Dialog>
    </>
  );
}
